import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { customInstance } from '@/lib/axios-instance';
import { API_CONFIG } from '@/constants/api';
import { processError } from '@/lib/error-utils';

interface Recipient {
  id: number;
  recipientId: string;
  fullName: string;
  emailId: string;
  mobilePhoneNo: string;
  status: string;
  outcome?: string;
  outcomeDetails?: string;
  updatedAt?: string;
}

interface ChatHistoryItem {
  id: string;
  correlationId: string;
  recipientId: string;
  channel: string;
  status: string;
  userMessage: string;
  receivedAt: string;
  imResponse: string;
  emailResponse: string;
  respondedAt: string;
  responseTime: number;
  aiRequestAt: string;
  aiResponseAt: string;
  aiResponseTime: number;
  createdAt: string;
}

interface UseChatHistoryProps {
  onSuccess: (chatHistoryData: ChatHistoryItem[], recipient?: Recipient | { transId: number }) => void;
  onClearSuccess?: () => void;
}

/**
 * Custom hook for fetching and managing chat history data
 * 
 * @param onSuccess - Callback function called when chat history is successfully fetched
 * @param onClearSuccess - Optional callback function called when chat history is successfully cleared
 * @returns Object containing fetchChatHistory, clearChatHistory functions and loading states
 * 
 * @example
 * ```tsx
 * const { fetchChatHistory, clearChatHistory, isLoadingChatHistory, isClearingChat } = useChatHistory({
 *   onSuccess: (chatHistoryData, recipient) => {
 *     setChatHistoryData(chatHistoryData);
 *     setCurrentRecipientForChat(recipient);
 *     setIsChatHistoryDialogOpen(true);
 *   },
 *   onClearSuccess: () => {
 *     refetchCandidates();
 *   }
 * });
 * 
 * // Usage with recipient object
 * fetchChatHistory(recipient.id, recipient);
 * 
 * // Usage with just transId (for tpmo page)
 * fetchChatHistory(transId);
 * 
 * // Clear chat history
 * clearChatHistory('CANDIDATE_OUTREACH', recipientId);
 * ```
 */
export const useChatHistory = ({ onSuccess, onClearSuccess }: UseChatHistoryProps) => {
  const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(false);
  const [isClearingChat, setIsClearingChat] = useState(false);

  const groupChatHistoryByCorrelationId = (chatHistory: any[]): ChatHistoryItem[] => {
    // The new backend data structure is already in the correct format
    // Each item represents a complete conversation with request/response
    const processedData = chatHistory.map((item) => ({
      id: item.id,
      correlationId: item.correlationId,
      recipientId: item.recipientId,
      channel: item.channel,
      status: item.status,
      userMessage: item.request || '',
      receivedAt: item.requestAt || item.createdAt || '',
      imResponse: item.response || '',
      emailResponse: '', // Not present in new structure, keeping for compatibility
      respondedAt: item.responseAt || '',
      responseTime: item.responseTime,
      aiRequestAt: item.aiRequestAt,
      aiResponseAt: item.aiResponseAt,
      aiResponseTime: item.aiResponseTime,
      createdAt: item.createdAt
    }));

    return processedData;
  };

  /**
   * Fetches chat history for a given reference ID
   * 
   * @param referenceId - The reference ID (recipient.id or transId)
   * @param recipient - Optional recipient object (required for recipient pages, not needed for tpmo page)
   * @param agentType - Optional agent type to filter chat history (e.g., 'TPMO', 'CANDIDATE_OUTREACH')
   */
  const fetchChatHistory = async (referenceId: string | number, recipient?: Recipient, agentType?: string) => {
    setIsLoadingChatHistory(true);
    try {
      const url = agentType
        ? `${API_CONFIG.ENDPOINTS.TRANS_MASTER}/chat-history/referenceId/${referenceId}?agentType=${agentType}`
        : `${API_CONFIG.ENDPOINTS.TRANS_MASTER}/chat-history/referenceId/${referenceId}`;

      const response = await customInstance<Record<string, any>>({
        url,
        method: 'GET'
      });

      // Handle different possible response structures
      let chatHistoryArray = null;

      if (response.data.status === 'success' && response.data.chatHistory) {
        chatHistoryArray = response.data.chatHistory;
      } else if (Array.isArray(response.data)) {
        // Direct array response
        chatHistoryArray = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        // Data wrapped in 'data' property
        chatHistoryArray = response.data.data;
      }

      if (chatHistoryArray && chatHistoryArray.length > 0) {
        const groupedData = groupChatHistoryByCorrelationId(chatHistoryArray);

        // Call the success callback with the data
        if (recipient) {
          onSuccess(groupedData, recipient);
        } else {
          // For the tpmo page case where we pass transId
          onSuccess(groupedData, { transId: referenceId as number });
        }
      } else {
        toast.error("No chat history found");
      }
    } catch (error: any) {
      console.error('Error fetching chat history:', error);
      const processedError = processError(error);
      toast.error(`${processedError.title}: ${processedError.message}`);
    } finally {
      setIsLoadingChatHistory(false);
    }
  };

  /**
   * Clears chat history for a given agent type and recipient ID
   * 
   * @param agentType - The agent type (e.g., 'CANDIDATE_OUTREACH')
   * @param recipientId - The recipient ID
   */
  const clearChatHistory = async (agentType: string, recipientId: number) => {
    setIsClearingChat(true);
    try {
      const response = await customInstance<any>({
        url: `${API_CONFIG.ENDPOINTS.TEMP_CANDIDATE}/clear/agentType/${agentType}/recipientId/${recipientId}`,
        method: 'PUT'
      });

      // Display success message from backend
      if (response.data) {
        toast.success(response.data);
      } else {
        toast.success("Chat history cleared successfully");
      }

      // Call the success callback if provided
      if (onClearSuccess) {
        onClearSuccess();
      }

    } catch (error: any) {
      console.error('Error clearing chat history:', error);
      const processedError = processError(error);
      toast.error(`${processedError.title}: ${processedError.message}`);
    } finally {
      setIsClearingChat(false);
    }
  };

  return {
    fetchChatHistory,
    clearChatHistory,
    isLoadingChatHistory,
    isClearingChat
  };
};